import { useState } from "react";
import { Mail, Phone, MapPin, Building2, Reply, StickyNote } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FollowUpBadge } from "./FollowUpBadge";
import { ActivityTimeline } from "./ActivityTimeline";
import { DeleteLeadButton } from "./DeleteLeadButton";

export const STATUSES = ["New", "Contacted", "Proposal Sent", "Closed"];

const STATUS_STYLES = {
  New: "bg-cyan-50 text-cyan-800 border-cyan-200",
  Contacted: "bg-amber-50 text-amber-800 border-amber-200",
  "Proposal Sent": "bg-violet-50 text-violet-800 border-violet-200",
  Closed: "bg-slate-100 text-slate-700 border-slate-300",
};

const Tag = ({ label, value }) => (
  <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1 text-slate-700">
    <span className="text-slate-500">{label}:</span> {value}
  </span>
);

export const LeadCard = ({ lead, onStatus, onReply, onAddNote, onFollowUp, onDelete, selected, onSelect }) => {
  const [notesOpen, setNotesOpen] = useState(false);
  return (
  <div
    data-testid={`admin-lead-row-${lead.reference}`}
    data-selected={selected ? "true" : "false"}
    className={`rounded-2xl border bg-white shadow-sm p-6 transition-all ${selected ? "border-cyan-500 ring-2 ring-cyan-100" : "border-slate-200 hover:border-cyan-300"}`}
  >
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        <Checkbox
          data-testid={`admin-select-${lead.reference}`}
          checked={selected}
          onCheckedChange={(v) => onSelect(lead.id, !!v)}
          aria-label={`Select ${lead.name}`}
          className="mt-1.5 border-slate-400 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
        />
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-lg font-semibold text-slate-900">{lead.name}</h3>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_STYLES[lead.status]}`}>{lead.status}</span>
          <FollowUpBadge lead={lead} onDone={onFollowUp} />
          <span className="font-mono text-xs text-slate-500">{lead.reference}</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-base text-slate-600">
          {lead.organization && (
            <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4 text-cyan-600" />{lead.organization}</span>
          )}
          <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 hover:text-cyan-700"><Mail className="w-4 h-4 text-cyan-600" />{lead.email}</a>
          {lead.phone && (
            <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 hover:text-cyan-700"><Phone className="w-4 h-4 text-cyan-600" />{lead.phone}</a>
          )}
          <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-cyan-600" />{lead.region}</span>
        </div>
      </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid={`admin-note-button-${lead.reference}`}
          onClick={() => setNotesOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white hover:border-cyan-500 text-slate-800 text-sm font-semibold px-3.5 py-2 transition-colors"
        >
          <StickyNote className="w-4 h-4" /> Add note
        </button>
        <button
          type="button"
          data-testid={`admin-reply-button-${lead.reference}`}
          onClick={() => onReply(lead)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold px-3.5 py-2 transition-colors"
        >
          <Reply className="w-4 h-4" /> Reply
        </button>
        <Select value={lead.status} onValueChange={(v) => onStatus(lead.id, v)}>
          <SelectTrigger data-testid={`admin-status-select-${lead.reference}`} className="w-[170px] bg-white border-slate-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s} data-testid={`status-option-${lead.reference}-${s}`}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DeleteLeadButton lead={lead} onDelete={onDelete} />
      </div>
    </div>

    <div className="mt-4 flex flex-wrap gap-2 text-xs">
      <Tag label="Waterbody" value={lead.waterbody_type} />
      {lead.surface_acres && <Tag label="Acres" value={lead.surface_acres} />}
      {lead.goal && <Tag label="Goal" value={lead.goal} />}
      <Tag label="Received" value={new Date(lead.created_at).toLocaleDateString()} />
    </div>

    {lead.message && (
      <p className="mt-4 text-base text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4 leading-relaxed">{lead.message}</p>
    )}

    <ActivityTimeline lead={lead} onAddNote={onAddNote} open={notesOpen} setOpen={setNotesOpen} />
  </div>
  );
};
